/**
 * FirstRunSteps — SIGNUP-3: every setup screen from the redesign,
 * rendered on the first-run kit (components/firstrun) and bound to
 * OnboardingWizard's existing state, validation and save paths.
 *
 * The screens are presentational. The wizard owns what happens on
 * Continue; a screen only shows what it's given and calls back. The
 * copy names real Orbit things — the plans, prices and features come
 * from services/billing; the craft list is the app's CREATIVE_ROLES;
 * the import sources are the ones the importer actually reads.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Nova3D } from './steps/Nova3D';
import {
  FirstRunFrame, StageBlock, StageCapsule, StageChip, StageCTA, StageHint, StageRow, StageText,
} from '@/components/firstrun/Stage';
import { enterStage, flyChipToCapsule, swapStage } from '@/components/firstrun/stageMotion';
import { CRAFT, IC, type CraftKey } from '@/components/firstrun/tokens';
import { CREATIVE_ROLES, type CreativeRole } from '@/lib/onboardingPersonalization';
import { PLAN_FEATURES, PLAN_LABELS, PLAN_PRICING, PLAN_TAGLINES, type SubscriptionPlan } from '@/services/billing';
import { TRIAL_DAYS } from '@/config/launch';
import type { FormHome } from './steps/FirstFormStep';

// ── Shared ─────────────────────────────────────────────────────────

/** Which craft family a role belongs to — sets the accent for the rest of setup. */
export function craftOf(role: CreativeRole | null): CraftKey {
  switch (role) {
    case 'photographer':
    case 'food_photographer':
      return 'photo';
    case 'videographer':
    case 'vlogger':
    case 'drone_pilot':
    case 'stop_motion':
      return 'film';
    case 'graphic_designer':
    case 'other':
      return 'other';
    default:
      return 'photo';
  }
}

const ROLE_LABEL: Record<CreativeRole, string> = {
  photographer: 'Photographer',
  videographer: 'Videographer',
  drone_pilot: 'Drone pilot',
  food_photographer: 'Food photographer',
  vlogger: 'Vlogger / creator',
  stop_motion: 'Stop-motion',
  graphic_designer: 'Graphic designer',
  other: 'Something else',
};

const ROLE_SWATCH: Record<CreativeRole, string> = {
  photographer: CRAFT.photo.acc,
  food_photographer: '#F0C05A',
  videographer: CRAFT.film.acc,
  vlogger: '#7ECFCF',
  drone_pilot: '#6A8AF0',
  stop_motion: '#B45BC8',
  graphic_designer: '#F29A7E',
  other: CRAFT.other.acc,
};

export interface StepNav {
  onNext: () => void;
  onBack?: () => void;
  onSkip?: () => void;
  busy?: boolean;
  /** "02 / 05" for required steps; "Optional" otherwise. */
  count: string;
  accent: string;
  palette: string;
  onSkipAll?: () => void;
}

/**
 * A step screen: runs the enter motion when it mounts and the gather
 * motion before handing control back on Continue.
 */
function useStepMotion(key: string) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    enterStage(rootRef.current, 1);
  }, [key]);
  const forward = (apply: () => void) => swapStage(rootRef.current, 1, apply);
  const back = (apply: () => void) => swapStage(rootRef.current, -1, apply);
  return { rootRef, forward, back };
}

function Screen({
  stepKey, nav, stage, text, cta, stageMaxWidth, right,
}: {
  stepKey: string;
  nav: StepNav;
  stage: ReactNode;
  text: ReactNode;
  cta: (go: (fn: () => void) => void) => ReactNode;
  stageMaxWidth?: string;
  right?: { label: string; onClick: () => void };
}) {
  const { rootRef, forward, back } = useStepMotion(stepKey);
  return (
    <FirstRunFrame
      rootRef={rootRef}
      palette={nav.palette}
      accent={nav.accent}
      onBack={nav.onBack ? () => back(nav.onBack!) : undefined}
      right={right ?? (nav.onSkipAll ? { label: 'Skip setup', onClick: nav.onSkipAll } : undefined)}
      stageMaxWidth={stageMaxWidth}
      stage={stage}
      text={text}
      cta={cta(forward)}
    />
  );
}

// ── 01 Welcome ─────────────────────────────────────────────────────

export function WelcomeStage({ nav, firstName, studioName }: { nav: StepNav; firstName?: string; studioName?: string }) {
  return (
    <Screen
      stepKey="welcome"
      nav={nav}
      stage={
        <>
          <StageRow dx={9}><StageChip label="Your contact" index={0} accent={nav.accent} /></StageRow>
          <StageRow dx={-10}><StageChip label="Your craft" index={1} accent={nav.accent} /></StageRow>
          <StageCapsule value={studioName || 'Your studio'} nova="idle" focus accent={nav.accent} />
          <StageRow dx={7}><StageChip label="Your business" index={2} accent={nav.accent} /></StageRow>
          <StageRow dx={-6}><StageChip label="Your plan" index={3} accent={nav.accent} /></StageRow>
        </>
      }
      text={
        <StageText count={nav.count} label="Welcome" icon={IC.home} accent={nav.accent}
          title={firstName ? `Welcome to Orbit, ${firstName}.` : 'Welcome to Orbit.'}
          body="Five short questions and Orbit is ready for your business." />
      }
      cta={(go) => <StageCTA primary="Let's set up your business" onPrimary={() => go(nav.onNext)} />}
    />
  );
}

// ── 02 Contact ─────────────────────────────────────────────────────

export function ContactStage({ nav, email, phone, onEmail, onPhone, errors }: {
  nav: StepNav; email: string; phone: string; onEmail: (v: string) => void; onPhone: (v: string) => void;
  errors: { email?: string; phone?: string };
}) {
  const err = errors.email || errors.phone;
  return (
    <Screen
      stepKey="contact"
      nav={nav}
      stage={
        <>
          <StageCapsule label="Email" value={email} editable onChange={onEmail} inputType="email" autoComplete="email" placeholder="you@yourbusiness.com" focus accent={nav.accent} />
          <StageCapsule label="Phone" value={phone} editable onChange={onPhone} inputType="tel" autoComplete="tel" placeholder="+1 (555) 000-0000" accent={nav.accent} />
          <StageHint>{err ? <span role="alert" className="text-red-300/90">{err}</span> : 'Both go on your quotes and invoices, so clients know how to reach you.'}</StageHint>
        </>
      }
      text={
        <StageText count={nav.count} label="Contact" icon={IC.phone} accent={nav.accent}
          title="How should clients reach you?"
          body="Your email and number go on quotes and invoices. Change them any time in Settings." />
      }
      cta={(go) => <StageCTA primary="Continue" loading={nav.busy} onPrimary={() => go(nav.onNext)} />}
    />
  );
}

// ── 03 Craft ───────────────────────────────────────────────────────

export function CraftStage({ nav, role, onRole, otherText, onOtherText }: {
  nav: StepNav; role: CreativeRole | null; onRole: (r: CreativeRole) => void; otherText: string; onOtherText: (v: string) => void;
}) {
  const roles = CREATIVE_ROLES;
  const rows: CreativeRole[][] = [roles.slice(0, 2) as CreativeRole[], roles.slice(2, 5) as CreativeRole[], roles.slice(5, 8) as CreativeRole[]];
  const craft = craftOf(role);
  const canGo = !!role && (role !== 'other' || otherText.trim().length > 0);
  return (
    <Screen
      stepKey="craft"
      nav={nav}
      stage={
        <>
          {rows.map((r, i) => (
            <StageRow key={i} dx={[4, -4, 3][i]}>
              {r.map((id, j) => (
                <StageChip key={id} label={ROLE_LABEL[id]} big={i === 0} tall={i > 0} swatch={ROLE_SWATCH[id]} selected={role === id} accent={ROLE_SWATCH[id]} noCheck onClick={() => onRole(id)} index={i * 3 + j} />
              ))}
            </StageRow>
          ))}
          {role === 'other' ? (
            <StageCapsule label="Your work" value={otherText} editable onChange={onOtherText} placeholder="Tell us in a few words" focus accent={nav.accent} />
          ) : (
            <StageHint>{role ? CRAFT[craft].desc : 'Pick the one closest to what you do most.'}</StageHint>
          )}
        </>
      }
      text={
        <StageText count={nav.count} label="Your craft" icon={IC.camera} accent={nav.accent}
          title="What kind of work do you do?"
          body="Orbit shapes your projects, contracts and galleries around it. Your pick sets the colour from here on." />
      }
      cta={(go) => <StageCTA primary="Continue" disabled={!canGo} loading={nav.busy} onPrimary={() => go(nav.onNext)} />}
    />
  );
}

// ── 04 Studio ──────────────────────────────────────────────────────

export function StudioStage({ nav, name, slug, onName, onSlug, errors, origin }: {
  nav: StepNav; name: string; slug: string; onName: (v: string) => void; onSlug: (v: string) => void;
  errors: { orgName?: string; orgSlug?: string }; origin: string;
}) {
  const err = errors.orgName || errors.orgSlug;
  return (
    <Screen
      stepKey="studio"
      nav={nav}
      stage={
        <>
          <StageCapsule label="Business" value={name} editable onChange={onName} placeholder="Your business name" autoComplete="organization" focus accent={nav.accent} />
          <StageCapsule label="Link" value={slug} editable onChange={onSlug} placeholder="your-business" accent={nav.accent} />
          <StageHint>
            {err ? <span role="alert" className="text-red-300/90">{err}</span> : <>Your forms and portals live at <span className="text-[#F2EFEB]">{origin.replace(/^https?:\/\//, '')}/forms/{slug || 'your-business'}</span>. Letters, numbers and hyphens.</>}
          </StageHint>
        </>
      }
      text={
        <StageText count={nav.count} label="Business" icon={IC.pin} accent={nav.accent}
          title="Set up your business."
          body="This is how your business appears on everything clients see. Nothing here is set in stone." />
      }
      cta={(go) => <StageCTA primary="Continue" disabled={!name.trim()} loading={nav.busy} onPrimary={() => go(nav.onNext)} />}
    />
  );
}

// ── 05 Import ──────────────────────────────────────────────────────

export const IMPORT_SOURCES: Array<{ id: string; name: string; how: string }> = [
  { id: 'pixieset', name: 'Pixieset', how: 'Studio → Contacts → the ⋯ menu → Export CSV' },
  { id: 'honeybook', name: 'HoneyBook', how: 'Contacts → Export → All contacts (.csv)' },
  { id: 'dubsado', name: 'Dubsado', how: 'Clients → the ⋯ menu → Export clients' },
  { id: 'tave', name: 'Táve', how: 'Contacts → Filter → Export → Comma separated' },
  { id: 'studio_ninja', name: 'Studio Ninja', how: 'Clients → Export → Download CSV' },
  { id: 'other', name: 'Something else', how: 'Any CSV with a name column works. Email and phone come across too.' },
];

export function ImportStage({ nav, importing, imported, held, names, progress, onFile, onReset }: {
  nav: StepNav;
  importing: boolean;
  /** Clients from the file, once it's read (imported, or held). */
  imported: number | null;
  /**
   * CSV-IMPORT-1 — they're HELD, not in the roster yet: the business is
   * on Free until the plan step (3 clients), so they come across the
   * moment the plan starts (components/onboarding/PendingClientImport).
   */
  held?: boolean;
  /** A few client names from the file, for the chips. */
  names: string[];
  progress: number;
  onFile: (file: File) => void;
  /** Start over with a different file. */
  onReset?: () => void;
}) {
  const [source, setSource] = useState<string>('pixieset');
  const fileRef = useRef<HTMLInputElement>(null);
  const src = IMPORT_SOURCES.find((s) => s.id === source) ?? IMPORT_SOURCES[0];
  const done = imported !== null;
  const count = `${imported ?? 0} client${imported === 1 ? '' : 's'}`;
  return (
    <Screen
      stepKey={`import-${done ? (held ? 'held' : 'done') : importing ? 'busy' : 'pick'}`}
      nav={nav}
      stage={
        importing || done ? (
          <>
            {names[0] && <StageRow dx={8}><StageChip label={names[0]} selected accent={nav.accent} index={0} /></StageRow>}
            {names[1] && <StageRow dx={-9}><StageChip label={names[1]} selected accent={nav.accent} index={1} /></StageRow>}
            <StageCapsule
              label={done ? (held ? 'Ready' : 'Imported') : 'Reading'}
              value={done ? (held ? `${count} ready` : `${count} came across`) : 'Reading your file…'}
              nova={done ? (held ? 'happy' : 'celebrating') : 'thinking'}
              progress={done ? 100 : progress}
              focus
              accent={nav.accent}
            />
            {names[2] && <StageRow dx={6}><StageChip label={names[2]} accent={nav.accent} index={2} /></StageRow>}
            <StageHint>
              {done
                ? held
                  ? "They'll come across the moment your plan starts. Nobody on the list gets an email."
                  : "They're in your roster now — nothing else to do here."
                : 'Matching the columns…'}
            </StageHint>
          </>
        ) : (
          <>
            <StageRow dx={-3}>
              {IMPORT_SOURCES.slice(0, 3).map((s, i) => (
                <StageChip key={s.id} label={s.name} tall selected={source === s.id} accent={nav.accent} onClick={() => setSource(s.id)} index={i} />
              ))}
            </StageRow>
            <StageRow dx={4}>
              {IMPORT_SOURCES.slice(3).map((s, i) => (
                <StageChip key={s.id} label={s.name} tall selected={source === s.id} accent={nav.accent} onClick={() => setSource(s.id)} index={3 + i} />
              ))}
            </StageRow>
            <StageHint>{src.how}</StageHint>
            <StageBlock kind="cap">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex h-[58px] w-full max-w-[580px] items-center justify-center gap-3 rounded-full text-[16px] font-medium text-[#F2EFEB]"
                style={{ background: 'linear-gradient(180deg, rgba(120,104,190,.62), rgba(80,64,150,.58))', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.34),inset 0 0 0 1px rgba(255,255,255,.16),0 20px 50px rgba(10,4,40,.45)', backdropFilter: 'blur(24px)' }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden><path d={IC.upload} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Choose the CSV
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv,.txt,text/plain"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // Cleared, so picking the same file again (after fixing it) still fires.
                  e.target.value = '';
                  if (f) onFile(f);
                }}
              />
            </StageBlock>
            <StageHint>We match the columns for you. Nobody on the list gets an email.</StageHint>
          </>
        )
      }
      text={
        <StageText count={nav.count} label="Clients" icon={IC.users} accent={nav.accent}
          title={done ? (held ? 'Your people are ready.' : 'Your people are here.') : 'Bring your people with you.'}
          body={
            done
              ? held
                ? "They'll be in your roster as soon as your plan starts — names, emails and phone numbers."
                : 'Their names, emails and phone numbers are in your roster.'
              : 'Import past clients so their names, emails and phone numbers are here when you need them.'
          } />
      }
      cta={(go) => (
        <StageCTA
          primary="Continue"
          onPrimary={() => go(nav.onNext)}
          loading={importing}
          skip={importing ? undefined : done ? (onReset ? 'Use a different file' : undefined) : 'Skip for now'}
          onSkip={done ? onReset : () => go(nav.onSkip ?? nav.onNext)}
        />
      )}
    />
  );
}

// ── 06 First form ──────────────────────────────────────────────────

const FORM_PRESETS: Record<CraftKey, Array<{ name: string; fields: string[] }>> = {
  photo: [
    { name: 'Wedding inquiry', fields: ['Your names', 'Wedding date', 'Venue', 'How did you find me?'] },
    { name: 'Portrait session', fields: ['Your name', 'Who is the session for?', 'Dates that suit you', 'Anything I should know?'] },
    { name: 'Brand shoot', fields: ['Company', 'What you need', 'Where it will be used', 'Budget range'] },
  ],
  film: [
    { name: 'Wedding film', fields: ['Your names', 'Wedding date', 'Venue', 'Ceremony only, or the full day?'] },
    { name: 'Brand film brief', fields: ['Company', 'What the film is for', 'Length', 'Budget range'] },
    { name: 'Documentary', fields: ['Working title', 'Who or what it follows', 'Timeline', 'Where it will screen'] },
  ],
  both: [
    { name: 'Wedding inquiry', fields: ['Your names', 'Wedding date', 'Venue', 'Photo, film, or both?'] },
    { name: 'Brand project', fields: ['Company', 'What you need', 'Where it will be used', 'Budget range'] },
    { name: 'Portrait session', fields: ['Your name', 'Who is the session for?', 'Dates that suit you', 'Anything I should know?'] },
  ],
  other: [
    { name: 'New project inquiry', fields: ['Your name', 'What you need', 'Timeline', 'Budget range'] },
    { name: 'Collaboration', fields: ['Your name', 'What you have in mind', 'Where it will be used', 'Anything I should know?'] },
    { name: 'Quote request', fields: ['Company', 'Scope', 'Deadline', 'Budget range'] },
  ],
};

export function FormStage({ nav, craft, formName, home, onName, onHome, studioName }: {
  nav: StepNav; craft: CraftKey; formName: string; home: FormHome; onName: (v: string) => void; onHome: (h: FormHome) => void; studioName: string;
}) {
  const presets = FORM_PRESETS[craft];
  const active = presets.find((p) => p.name === formName) ?? presets[0];
  return (
    <Screen
      stepKey="form"
      nav={nav}
      stage={
        <>
          <StageRow>
            {presets.map((p, i) => (
              <StageChip key={p.name} label={p.name} selected={formName === p.name} accent={nav.accent} onClick={() => onName(p.name)} index={i} />
            ))}
          </StageRow>
          <StageBlock>
            <div className="flex w-full max-w-[340px] flex-col gap-[11px] rounded-3xl p-5 text-left text-[#1A1422]" style={{ background: 'rgba(248,245,255,.94)', boxShadow: '0 20px 50px rgba(30,10,60,.35)' }}>
              <div className="flex justify-between text-[11px] font-semibold uppercase tracking-[.1em] text-[#1A1422]/55"><span>{studioName || 'Your studio'}</span><span>Live preview</span></div>
              <div className="text-[21px] font-bold tracking-[-0.02em]">{formName || active.name}</div>
              {active.fields.map((f) => (
                <div key={f} className="flex flex-col gap-[5px]"><span className="text-[11px] text-[#1A1422]/60">{f}</span><span className="h-7 rounded-[10px] bg-[#1A1422]/[0.06]" /></div>
              ))}
              <span className="mt-0.5 self-start rounded-full px-4 py-[9px] text-[12px] font-semibold text-[#1A1422]" style={{ background: nav.accent }}>Send inquiry</span>
            </div>
          </StageBlock>
          <StageRow dx={0}>
            <StageChip label="Embed it in my site" tall selected={home === 'embed'} accent={nav.accent} onClick={() => onHome('embed')} index={4} />
            <StageChip label="Use a page Orbit hosts" tall selected={home === 'hosted'} accent={nav.accent} onClick={() => onHome('hosted')} index={5} />
          </StageRow>
          <StageHint>{home === 'embed' ? 'One line of code on your site. It takes your fonts and colours.' : 'A link for a bio, a DM or an email. Works before you have a site at all.'}</StageHint>
        </>
      }
      text={
        <StageText count={nav.count} label="Inquiry form" icon={IC.doc} accent={nav.accent}
          title="Your first inquiry form."
          body="New clients fill it in from your site or a link you share. Pick a place to start; every field is editable later." />
      }
      cta={(go) => <StageCTA primary="Use this form" loading={nav.busy} onPrimary={() => { if (!formName) onName(active.name); go(nav.onNext); }} skip="Skip for now" onSkip={() => go(nav.onSkip ?? nav.onNext)} />}
    />
  );
}

// ── 08 Meet Nova ───────────────────────────────────────────────────

const NOVA_ANSWER = "Once you send invoices, I'll tell you the day one is late, and draft the reminder for you.";

export function NovaStage({ nav }: { nav: StepNav }) {
  const [asked, setAsked] = useState(false);
  const [typed, setTyped] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    enterStage(rootRef.current, 1);
  }, []);
  useEffect(() => {
    if (!asked) return;
    const t = setInterval(() => setTyped((n) => (n >= NOVA_ANSWER.length ? n : n + 1)), 32);
    return () => clearInterval(t);
  }, [asked]);
  const done = typed >= NOVA_ANSWER.length;
  const ask = () => flyChipToCapsule(rootRef.current, chipRef.current).then(() => setAsked(true));
  return (
    <FirstRunFrame
      rootRef={rootRef}
      palette={nav.palette}
      accent={nav.accent}
      onBack={nav.onBack ? () => swapStage(rootRef.current, -1, nav.onBack!) : undefined}
      right={nav.onSkipAll ? { label: 'Skip setup', onClick: nav.onSkipAll } : undefined}
      stage={
        <>
          <StageBlock><Nova3D state={done ? 'happy' : asked ? 'thinking' : 'curious'} fallbackMood={done ? 'happy' : 'curious'} size={150} /></StageBlock>
          <StageRow dx={9}><StageChip label="Answers questions" index={0} accent={nav.accent} /></StageRow>
          <StageRow dx={-7}><StageChip label="Drafts emails and contracts" index={1} accent={nav.accent} /></StageRow>
          <StageCapsule value={asked ? NOVA_ANSWER.slice(0, typed) || ' ' : 'Ask Nova about your business'} nova={done ? 'happy' : asked ? 'thinking' : 'has-suggestion'} focus accent={nav.accent} />
          {done && (
            <StageBlock>
              <div className="max-w-[440px] rounded-3xl px-5 py-4 text-left" style={{ background: 'rgba(240,234,255,.9)', color: '#2A1D48', boxShadow: '0 10px 30px rgba(30,10,60,.28)', animation: 'frRise .7s cubic-bezier(.16,.84,.24,1) both' }}>
                <div className="text-[12px] font-semibold uppercase tracking-[.08em] text-[#6E43C4]">Nova</div>
                <div className="mt-1.5 text-[15px] leading-[1.45]">She never sends anything on your behalf without you seeing it first — and she's on every paid plan.</div>
              </div>
            </StageBlock>
          )}
          <StageRow dx={6}><StageChip label="Tags your photos" index={2} accent={nav.accent} /></StageRow>
          {!asked && (
            <>
              <StageHint>Try it</StageHint>
              <StageRow><StageChip ref={chipRef} label="Which clients haven't paid yet?" tall accent={nav.accent} onClick={ask} index={3} /></StageRow>
            </>
          )}
        </>
      }
      text={
        <StageText count="Meet Nova" label="30 seconds" icon={IC.wand} accent={nav.accent}
          title="This is Nova."
          body="Nova knows your business. She reads your inbox, drafts the replies, reads amounts off receipts, tags your galleries and tells you what needs doing today — in your voice, not hers." />
      }
      cta={<StageCTA primary="Nice to meet you" onPrimary={() => swapStage(rootRef.current, 1, nav.onNext)} />}
    />
  );
}

// ── 09 Sites ───────────────────────────────────────────────────────

export function SitesStage({ nav, interested, onInterested, slug, studioName, craftLabel, origin }: {
  nav: StepNav; interested: boolean | null; onInterested: (v: boolean) => void; slug: string; studioName: string; craftLabel: string; origin: string;
}) {
  const on = interested === true;
  const host = origin.replace(/^https?:\/\//, '');
  return (
    <Screen
      stepKey="sites"
      nav={nav}
      stage={
        <>
          <StageCapsule label="Website" value={on ? `On · ${host}/s/${slug || 'your-business'}` : 'Off'} toggle on={on} onToggle={() => onInterested(!on)} focus accent={nav.accent} />
          <StageBlock>
            <div className="w-full max-w-[420px] overflow-hidden rounded-3xl text-left transition-opacity duration-300" style={{ opacity: on ? 1 : 0.35, background: 'rgba(20,12,30,.72)', backdropFilter: 'blur(20px)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.14),0 20px 50px rgba(20,6,40,.4)' }}>
              <div className="flex items-center gap-1.5 px-3.5 py-2.5" style={{ boxShadow: 'inset 0 -1px 0 rgba(255,255,255,.08)' }}>
                <span className="h-[7px] w-[7px] rounded-full bg-white/25" /><span className="h-[7px] w-[7px] rounded-full bg-white/25" />
                <span className="ml-2 text-[11px] text-[#F2EFEB]/60">{host}/s/{slug || 'your-business'}</span>
              </div>
              <div className="flex flex-col gap-3 p-3.5">
                <div className="grid place-items-center rounded-[14px] text-[11px] uppercase tracking-[.1em] text-[#F2EFEB]/55" style={{ height: 'clamp(90px, 9vw, 130px)', background: 'repeating-linear-gradient(135deg,rgba(255,255,255,.07) 0 1px,transparent 1px 10px)' }}>Your cover photo</div>
                <div className="flex items-end justify-between gap-3">
                  <div><div className="text-[20px] font-bold tracking-[-0.02em]">{studioName || 'Your studio'}</div><div className="mt-1 text-[12px] text-[#F2EFEB]/70">{craftLabel}</div></div>
                  <span className="rounded-full px-3.5 py-2 text-[11px] font-semibold text-[#1A1422]" style={{ background: nav.accent }}>Enquire</span>
                </div>
              </div>
            </div>
          </StageBlock>
        </>
      }
      text={
        <StageText count={nav.count} label="Site" icon={IC.globe} accent={nav.accent}
          title="Give your business a front door."
          body="A one-page site with your work, your prices and your inquiry form. Turn it on now or any time from Sites." />
      }
      cta={(go) => <StageCTA primary="Continue" onPrimary={() => { if (interested === null) onInterested(false); go(nav.onNext); }} skip="Skip for now" onSkip={() => go(nav.onSkip ?? nav.onNext)} />}
    />
  );
}

// ── 10 Expenses ────────────────────────────────────────────────────

export function ExpensesStage({ nav, seed, onSeed, categories }: { nav: StepNav; seed: boolean; onSeed: (v: boolean) => void; categories: readonly string[] }) {
  return (
    <Screen
      stepKey="expenses"
      nav={nav}
      stage={
        <>
          <StageRow dx={6}>{categories.slice(0, 3).map((c, i) => <StageChip key={c} label={c} selected={seed} accent={nav.accent} index={i} />)}</StageRow>
          <StageCapsule label="Categories" value={seed ? 'Start me off with the usual ones' : "I'll add my own"} toggle on={seed} onToggle={() => onSeed(!seed)} focus accent={nav.accent} />
          <StageRow dx={-5}>{categories.slice(3, 6).map((c, i) => <StageChip key={c} label={c} selected={seed} accent={nav.accent} index={3 + i} />)}</StageRow>
          <StageHint>{seed ? 'Gear, software, travel and the rest — ready under Money. Rename or remove any of them.' : 'You can add categories the first time you log an expense.'}</StageHint>
        </>
      }
      text={
        <StageText count={nav.count} label="Expenses" icon={IC.receipt} accent={nav.accent}
          title="What does it cost to run?"
          body="Orbit counts what you spend so you don't have to. Start with the usual categories, or build your own." />
      }
      cta={(go) => <StageCTA primary="Continue" onPrimary={() => go(nav.onNext)} skip="Skip for now" onSkip={() => go(nav.onSkip ?? nav.onNext)} />}
    />
  );
}

// ── 12 Recap ───────────────────────────────────────────────────────

// FOUNDERS-GO-3 — the navigation chip went with the navigation step: the
// rail is simply where everyone starts now, not a choice to recap.
export function ConvergeStage({ nav, craftLabel, importedCount, formName, studioName, goalText, siteOn, slug, cta }: {
  nav: StepNav; craftLabel: string; importedCount: number | null; formName: string; studioName: string; goalText: string | null; siteOn: boolean | null; slug: string;
  /** The button. "Choose your plan" unless there's nothing to choose (FOUNDERS-GO-4). */
  cta?: string;
}) {
  return (
    <Screen
      stepKey="converge"
      nav={nav}
      stage={
        <>
          <StageRow dx={6}><StageChip label={craftLabel} index={0} accent={nav.accent} /><StageChip label={importedCount ? `${importedCount} clients` : 'Clients · later'} index={1} accent={nav.accent} /></StageRow>
          <StageRow dx={-9}><StageChip label={formName || 'Inquiry form · later'} index={2} accent={nav.accent} /></StageRow>
          <StageCapsule value={studioName || 'Your studio'} nova="celebrating" tag={slug ? `/${slug}` : undefined} focus accent={nav.accent} />
          <StageRow dx={5}><StageChip label={goalText ? `Goal · ${goalText}` : 'Goal · later'} index={3} accent={nav.accent} /><StageChip label={siteOn ? 'Site on' : 'Site off'} index={4} accent={nav.accent} /></StageRow>
        </>
      }
      text={
        <StageText count="Recap" icon={IC.star} accent={nav.accent}
          title="All of it, in one place."
          body="Here's your business so far. You can change any of it later." />
      }
      cta={(go) => <StageCTA primary={cta ?? 'Choose your plan'} onPrimary={() => go(nav.onNext)} />}
    />
  );
}

// ── 13 Plan ────────────────────────────────────────────────────────

export type PaidPlan = Exclude<SubscriptionPlan, 'free'>;

/**
 * FOUNDING-3 — a Founding Member who came here from their founding page
 * ("Get the Founding Pass" with no Orbit account yet) sees the two
 * Passes first — Orbit and Orbit Business — with the one they chose on
 * their page selected. The wizard owns the numbers' source
 * (FOUNDING_PASS) and the checkout; this only draws the cards.
 */
export interface FoundingPassOption {
  tier: 'orbit' | 'business';
  /** "Orbit", "Orbit Business". */
  name: string;
  /** Paid once, for `years`. */
  price: number;
  /** A year after the Pass: the plan's yearly price less `afterPct`. */
  renewal: number;
}

export interface FoundingPassChoice {
  options: FoundingPassOption[];
  /** The Pass picked, or null when a regular plan is. */
  selected: FoundingPassOption['tier'] | null;
  onSelect: (tier: FoundingPassOption['tier']) => void;
  years: number;
  afterPct: number;
}

export function PlanStage({ nav, plans, selected, onSelect, interval, onInterval, suggested, lapsed, lapsedEnded, onSignOut, foundingPass, passOnly, onSeeOptions, passGone }: {
  nav: StepNav; plans: PaidPlan[]; selected: PaidPlan | null; onSelect: (p: PaidPlan) => void;
  interval: 'monthly' | 'yearly'; onInterval: (i: 'monthly' | 'yearly') => void; suggested: PaidPlan;
  lapsed?: boolean; lapsedEnded?: string | null; onSignOut?: () => void;
  foundingPass?: FoundingPassChoice;
  /**
   * FOUNDERS-GO-4 — the founder's own Pass and nothing else: no plans, no
   * Monthly/Yearly, no second Pass. They chose on their founding page,
   * and Rennan's rule is that they aren't asked again.
   */
  passOnly?: boolean;
  /** "See other options" — back to the full step. */
  onSeeOptions?: () => void;
  /** A founder whose Pass closed or sold out before they got here. */
  passGone?: boolean;
}) {
  const money = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
  const cents = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fp = foundingPass;
  const passOn = !!fp?.selected;
  const pass = fp?.options.find((o) => o.tier === fp.selected) ?? null;
  const passName = pass?.name ?? 'Orbit';
  const only = !!passOnly && !!fp && !!pass;
  if (only && fp && pass) {
    const years = fp.years === 3 ? 'three' : String(fp.years);
    const Years = years.charAt(0).toUpperCase() + years.slice(1);
    return (
      <Screen
        stepKey="plan"
        nav={nav}
        stageMaxWidth="560px"
        stage={
          <StageBlock>
            {/* One card, centred — so it sits on the brightest part of the
                sky. Dark glass keeps the type legible there; the plan
                cards in the full step span wider and don't need it. */}
            <div
              className="w-full rounded-[28px] p-[22px] text-left text-[#F2EFEB] backdrop-blur-2xl md:p-[26px]"
              style={{ background: 'rgba(22,16,38,.58)', boxShadow: `inset 0 0 0 2px ${nav.accent}, 0 0 40px ${nav.accent}40` }}
            >
              <div className="text-[20px] font-bold tracking-[-0.02em]">{pass.name}</div>
              <div className="mt-3">
                <span className="text-[40px] font-bold tracking-[-0.035em]">{cents(pass.price)}</span>
                <span className="text-[15px] text-[#F2EFEB]/75"> once</span>
                <div className="mt-0.5 text-[13px] text-[#F2EFEB]/70">{Years} years of {pass.name} · no trial</div>
              </div>
              <div className="mt-4 flex flex-col gap-[9px] pt-4" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,.12)' }}>
                {[
                  `Then ${cents(pass.renewal)} a year — ${fp.afterPct}% off, for good`,
                  'Your Launch Edition kit ships with it',
                ].map((line) => (
                  <div key={line} className="flex gap-2.5 text-[14px] text-[#F2EFEB]/85">
                    <svg width="14" height="14" viewBox="0 0 12 12" fill="none" className="mt-0.5 shrink-0" aria-hidden><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke={nav.accent} strokeWidth="1.7" strokeLinecap="round" /></svg>
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </StageBlock>
        }
        text={
          <StageText count={nav.count} label="Founding Pass" icon={IC.coin} accent={nav.accent}
            title="Your Founding Pass."
            body={`The one you picked on your founding page: ${years} years of ${pass.name}, paid once today. Nothing more until ${new Date().getFullYear() + fp.years}.`}
          >
            {onSignOut && (
              <button type="button" data-a="text" onClick={onSignOut} className="mt-1 text-[13px] text-[#F2EFEB]/55 underline-offset-2 hover:underline">
                Not now — sign out and finish later
              </button>
            )}
          </StageText>
        }
        cta={(go) => (
          <StageCTA
            primary="Continue to payment"
            loading={nav.busy}
            onPrimary={() => go(nav.onNext)}
            skip={onSeeOptions ? 'See other options' : undefined}
            onSkip={onSeeOptions}
            note="Payment opens in a secure Stripe window. Charged once, today."
          />
        )}
      />
    );
  }
  return (
    <Screen
      stepKey="plan"
      nav={nav}
      stageMaxWidth="920px"
      stage={
        <>
          {fp ? (
            <StageBlock>
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-1 pb-3">
                <span className="text-[16px] font-bold tracking-[-0.01em] text-[#F2EFEB]">Founding Pass</span>
                <span className="text-[13.5px] text-[#F2EFEB]/65">
                  {fp.years} years, paid once · your Launch Edition kit ships with it
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {fp.options.map((o) => {
                  const on = fp.selected === o.tier;
                  return (
                    <button
                      key={o.tier}
                      type="button"
                      onClick={() => fp.onSelect(o.tier)}
                      aria-pressed={on}
                      className="flex w-full items-center justify-between gap-4 rounded-[28px] border-0 p-[22px] text-left text-[#F2EFEB] backdrop-blur-2xl"
                      style={{ background: on ? 'rgba(255,255,255,.18)' : 'rgba(255,255,255,.08)', boxShadow: on ? `inset 0 0 0 2px ${nav.accent}, 0 0 40px ${nav.accent}40` : 'inset 0 0 0 1px rgba(255,255,255,.16)' }}
                    >
                      <div className="min-w-0">
                        <div className="text-[20px] font-bold tracking-[-0.02em]">{o.name}</div>
                        <div className="mt-1 text-[14px] leading-snug text-[#F2EFEB]/75">
                          Then ${o.renewal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} a year — {fp.afterPct}% off, for good
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <span className="text-[30px] font-bold tracking-[-0.035em]">
                          ${o.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <div className="text-[13px] text-[#F2EFEB]/65">once · no trial</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </StageBlock>
          ) : null}
          <StageRow>
            <StageChip label="Monthly" selected={interval === 'monthly'} accent={nav.accent} onClick={() => onInterval('monthly')} index={0} />
            <StageChip label="Yearly · 2 months free" selected={interval === 'yearly'} accent={nav.accent} onClick={() => onInterval('yearly')} index={1} />
          </StageRow>
          <StageBlock>
            <div className="-mx-4 flex w-[calc(100%+32px)] snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 pt-1.5 md:mx-0 md:grid md:w-full md:grid-cols-3 md:overflow-visible md:px-0" style={{ scrollbarWidth: 'none' }}>
              {plans.map((id) => {
                const on = !passOn && selected === id;
                const pr = PLAN_PRICING[id];
                const yearly = interval === 'yearly' && pr.yearly != null;
                const perMonth = yearly ? (pr.yearly as number) / 12 : pr.monthly;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onSelect(id)}
                    className="flex shrink-0 basis-[82%] snap-center flex-col gap-3.5 rounded-[28px] border-0 p-[22px] text-left text-[#F2EFEB] backdrop-blur-2xl md:basis-auto"
                    style={{ background: on ? 'rgba(255,255,255,.18)' : 'rgba(255,255,255,.08)', boxShadow: on ? `inset 0 0 0 2px ${nav.accent}, 0 0 40px ${nav.accent}40` : 'inset 0 0 0 1px rgba(255,255,255,.16)' }}
                    aria-pressed={on}
                  >
                    <div className="flex min-h-[26px] items-center justify-between gap-2">
                      <span className="text-[20px] font-bold tracking-[-0.02em]">{PLAN_LABELS[id]}</span>
                      {id === suggested && <span className="rounded-full px-2.5 py-[5px] text-[11px] font-semibold text-[#1A1422]" style={{ background: nav.accent }}>Suggested for you</span>}
                    </div>
                    <div>
                      <span className="text-[40px] font-bold tracking-[-0.035em]">{money(perMonth)}</span>
                      <span className="text-[15px] text-[#F2EFEB]/70"> / month</span>
                      <div className="mt-0.5 text-[13px] text-[#F2EFEB]/65">{yearly ? `${money(pr.yearly as number)} billed once a year` : 'Billed monthly'}</div>
                    </div>
                    <div className="text-[15px] text-[#F2EFEB]/85">{PLAN_TAGLINES[id]}</div>
                    <div className="flex flex-col gap-[9px] pt-3.5" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,.12)' }}>
                      {PLAN_FEATURES[id].slice(0, 4).map((f) => (
                        <div key={f} className="flex gap-2.5 text-[14px] text-[#F2EFEB]/85">
                          <svg width="14" height="14" viewBox="0 0 12 12" fill="none" className="mt-0.5 shrink-0" aria-hidden><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke={nav.accent} strokeWidth="1.7" strokeLinecap="round" /></svg>
                          {f}
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </StageBlock>
        </>
      }
      text={
        <StageText count={nav.count} label="Plan" icon={IC.coin} accent={nav.accent}
          title={lapsed ? 'Your subscription ended.' : passOn ? 'Your Founding Pass.' : 'Choose your plan.'}
          body={lapsed
            ? `${lapsedEnded ? `Your Orbit plan ended on ${lapsedEnded}. ` : 'Your Orbit plan is no longer active. '}Nothing has been touched — pick a plan and you're straight back in.`
            : passOn
              ? `Three years of ${passName}, charged once today — no trial. Or pick a regular plan instead; every one starts with ${TRIAL_DAYS} days free.`
              : passGone
                ? `The Founding Passes are no longer available — but every plan starts with ${TRIAL_DAYS} days free.`
                : `Every plan starts with ${TRIAL_DAYS} days free. Move up or down whenever the work changes.`}
        >
          {onSignOut && !lapsed && (
            <button type="button" data-a="text" onClick={onSignOut} className="mt-1 text-[13px] text-[#F2EFEB]/55 underline-offset-2 hover:underline">
              Not now — sign out and finish later
            </button>
          )}
        </StageText>
      }
      cta={(go) => (
        <StageCTA
          primary={lapsed ? 'Reactivate' : 'Continue to payment'}
          loading={nav.busy}
          onPrimary={() => go(nav.onNext)}
          note={lapsed ? 'Billing picks up from today. Cancel any time — nothing you’ve built goes away.' : passOn ? 'Payment opens in a secure Stripe window. Charged once, today.' : 'Payment opens in a secure Stripe window. Cancel any time.'}
        />
      )}
    />
  );
}

// ── 14 Welcome aboard ──────────────────────────────────────────────

export function WelcomeAboardStage({ nav, firstName, studioName, onEnter, onSkipTour, planetName }: {
  nav: StepNav; firstName?: string; studioName: string;
  /** Primary — take the two-minute tour. */
  onEnter: () => void;
  /** SIGNUP-8 — secondary: skip the tour and open the studio now. It
   *  stays in Learn for whenever they want it. */
  onSkipTour?: () => void;
  planetName?: string | null;
}) {
  return (
    <Screen
      stepKey="welcome-aboard"
      nav={{ ...nav, onBack: undefined, onSkipAll: undefined }}
      stage={
        <>
          <StageBlock><Nova3D state="celebrating" fallbackMood="celebrating" size={150} /></StageBlock>
          <StageRow dx={8}><StageChip label="Contracts ready" selected accent={nav.accent} index={0} /></StageRow>
          <StageRow dx={-8}><StageChip label="Galleries ready" selected accent={nav.accent} index={1} /></StageRow>
          <StageCapsule value={`${studioName || 'Your studio'} is open`} nova="happy" focus accent={nav.accent} tag={planetName ? `${planetName} · charted` : undefined} />
          <StageRow dx={6}><StageChip label="Invoices ready" selected accent={nav.accent} index={2} /></StageRow>
        </>
      }
      text={
        <StageText count="All set" icon={IC.check} accent={nav.accent}
          title={firstName ? `Welcome aboard, ${firstName}.` : 'Welcome aboard.'}
          body={onSkipTour ? "You're all set. Take the two-minute tour, or go straight in — it stays in Learn." : "You're all set."} />
      }
      cta={() => (
        <StageCTA
          primary={onSkipTour ? 'Take the tour' : 'Open Orbit'}
          onPrimary={onEnter}
          skip={onSkipTour ? 'Skip for now — open Orbit' : undefined}
          onSkip={onSkipTour}
        />
      )}
    />
  );
}

